import { Chip } from "@legal-500/legal500-design-system";
import type { EntitySchema } from "./fieldSchema";
import styles from "../styles/chips.module.css";
import { formatDateLong, formatDateShort } from "../utils/dateFormatter";
import type { Subscription } from "../api/subscriptions.service";

// Re-export for convenience
export type { Subscription };

// Render helpers
const renderSiteChip = (value: Subscription["site"]): React.ReactNode => {
  if (!value) {
    return null;
  }
  return (
    <Chip size="small" variant="filled" className={styles.chipTeal}>
      {value}
    </Chip>
  );
};

const renderLanguageChip = (value: Subscription["language"]): React.ReactNode => {
  if (!value) {
    return null;
  }
  return (
    <Chip size="small" variant="filled" className={styles.chipPink}>
      {value}
    </Chip>
  );
};

const renderStatusChip = (
  value: Subscription["status"],
  subscription: Subscription,
): React.ReactNode => {
  let chipClass = styles.chipStatusActive;
  if (value === "cancelled") {
    chipClass = styles.chipStatusDeprecated;
  } else if (value === "pending") {
    chipClass = styles.chipStatusPending;
  }

  const needsOnboarding =
    value === "pending" &&
    subscription.package?.metadataQuantities &&
    Object.keys(subscription.package.metadataQuantities).length > 0;

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      <Chip size="small" variant="outlined" className={chipClass}>
        {value}
      </Chip>
      {needsOnboarding && (
        <Chip size="small" variant="filled" className={styles.chipOnboarding}>
          Configuration Required
        </Chip>
      )}
    </div>
  );
};

const renderOfficeId = (value: Subscription["metadata"]): React.ReactNode => {
  if (!value) {
    return <span className={styles.nullValue}>-</span>;
  }

  // Show office_id if present
  const officeId = value.office_id as number | string | undefined;
  if (officeId !== undefined) {
    return <span>{String(officeId)}</span>;
  }

  // Show count of office_contact_pairs if present
  const officeContactPairs = value.office_contact_pairs as Array<{ office_id: number }> | undefined;
  if (Array.isArray(officeContactPairs) && officeContactPairs.length > 0) {
    return (
      <span className={styles.metadataSummary}>
        {officeContactPairs.length} office{officeContactPairs.length !== 1 ? "s" : ""}
      </span>
    );
  }

  return <span className={styles.nullValue}>-</span>;
};

export const subscriptionSchema: EntitySchema<Subscription> = {
  id: {
    label: "ID",
  },
  firmId: {
    label: "Company ID",
    sortable: true,
    sortField: "firmId",
  },
  companyName: {
    label: "Company",
    sortable: false,
    render: (value: Subscription["companyName"]) => value || "",
  },
  packageId: {
    label: "Package ID",
  },
  package: {
    label: "Product",
    render: (value: Subscription["package"]) => value?.productCode || "",
    sortable: true,
    sortField: "productCode",
  },
  billingTaxonomyTermId: {
    label: "Billing Taxonomy Term ID",
  },
  siteStructureId: {
    label: "Country / Region",
  },
  site: {
    label: "Site",
    render: renderSiteChip,
    sortable: true,
    sortField: "site",
  },
  language: {
    label: "Language",
    render: renderLanguageChip,
    sortable: true,
    sortField: "language",
  },
  startDate: {
    label: "Start Date",
    render: (value: string) => (value ? formatDateShort(value) : ""),
    sortable: true,
    sortField: "startDate",
  },
  endDate: {
    label: "End Date",
    render: (value: string) => (value ? formatDateShort(value) : ""),
    sortable: true,
    sortField: "endDate",
  },
  status: {
    label: "Status",
    render: renderStatusChip,
    sortable: true,
    sortField: "status",
  },
  correlationId: {
    label: "Correlation ID",
  },
  metadata: {
    label: "Office ID",
    render: renderOfficeId,
  },
  isFullyConfigured: {
    label: "Fully Configured",
    render: (value: Subscription["isFullyConfigured"]) => (value ? "Yes" : "No"),
  },
  featuresNeedingConfiguration: {
    label: "Features Needing Configuration",
    render: (value: Subscription["featuresNeedingConfiguration"]) => value?.join(", ") || "",
  },
  createdAt: {
    label: "Created At",
    render: (value: string) => (value ? formatDateShort(value) : ""),
  },
  updatedAt: {
    label: "Updated At",
    render: (value: string) => (value ? formatDateShort(value) : ""),
  },
};

// Modal-specific schema with long dates
export const subscriptionSchemaModal: EntitySchema<Subscription> = {
  ...subscriptionSchema,
  startDate: {
    ...subscriptionSchema.startDate,
    render: (value: string) => (value ? formatDateLong(value) : ""),
  },
  endDate: {
    ...subscriptionSchema.endDate,
    render: (value: string) => (value ? formatDateLong(value) : ""),
  },
  createdAt: {
    ...subscriptionSchema.createdAt,
    render: (value: string) => (value ? formatDateLong(value) : ""),
  },
  updatedAt: {
    ...subscriptionSchema.updatedAt,
    render: (value: string) => (value ? formatDateLong(value) : ""),
  },
};
